/**
 * Utility for managing anonymous participant session storage with expiration
 * Only applies to anonymous (!isAuthenticated) sessions
 */

export interface StoredParticipantSession {
  participantId: string;
  sessionId: string;
  deviceUuid: string;
  displayName?: string;
  timestamp: number;
  expiresAt: number;
}

const STORAGE_KEY_PREFIX = 'sifly_participant_session_';
const SESSION_DURATION_HOURS = 10;
const SESSION_DURATION_MS = SESSION_DURATION_HOURS * 60 * 60 * 1000; // 10 hours in milliseconds

/**
 * Generate storage key for a specific session
 */
const getStorageKey = (sessionId: string): string => {
  return `${STORAGE_KEY_PREFIX}${sessionId}`;
};

/**
 * Save participant session to localStorage with expiration
 */
export const saveParticipantSession = (
  participantId: string,
  sessionId: string,
  deviceUuid: string,
  displayName?: string
): void => {
  try {
    const now = Date.now();
    const sessionData: StoredParticipantSession = {
      participantId,
      sessionId,
      deviceUuid,
      displayName,
      timestamp: now,
      expiresAt: now + SESSION_DURATION_MS
    };

    const storageKey = getStorageKey(sessionId);
    localStorage.setItem(storageKey, JSON.stringify(sessionData));
  } catch (error) {
    console.warn('Failed to save participant session to localStorage:', error);
  }
};

/**
 * Load participant session from localStorage if not expired
 */
export const loadParticipantSession = (sessionId: string): StoredParticipantSession | null => {
  try {
    const storageKey = getStorageKey(sessionId);
    const storedData = localStorage.getItem(storageKey);

    if (!storedData) {
      return null;
    }

    const sessionData: StoredParticipantSession = JSON.parse(storedData);
    const now = Date.now();

    // Check if session has expired
    if (now > sessionData.expiresAt) {
      // Remove expired session
      localStorage.removeItem(storageKey);
      return null;
    }

    // Refresh expiration time on access
    sessionData.expiresAt = now + SESSION_DURATION_MS;
    localStorage.setItem(storageKey, JSON.stringify(sessionData));

    return sessionData;
  } catch (error) {
    console.warn('Failed to load participant session from localStorage:', error);
    return null;
  }
};

/**
 * Remove specific participant session from localStorage
 */
export const removeParticipantSession = (sessionId: string): void => {
  try {
    const storageKey = getStorageKey(sessionId);
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.warn('Failed to remove participant session from localStorage:', error);
  }
};

/**
 * Clean up all expired participant sessions from localStorage
 * This should be called on app initialization or component mount
 */
export const cleanupExpiredParticipantSessions = (): void => {
  try {
    const now = Date.now();
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        try {
          const storedData = localStorage.getItem(key);
          if (storedData) {
            const sessionData: StoredParticipantSession = JSON.parse(storedData);

            if (now > sessionData.expiresAt) {
              keysToRemove.push(key);
            }
          }
        } catch (parseError) {
          keysToRemove.push(key);
        }
      }
    }

    // Remove expired/corrupted sessions
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    if (keysToRemove.length > 0) {
      console.log(`Cleaned up ${keysToRemove.length} expired participant sessions`);
    }
  } catch (error) {
    console.warn('Failed to cleanup expired participant sessions:', error);
  }
};

/**
 * Get all active (non-expired) participant sessions
 * Useful for debugging or administrative purposes
 */
export const getAllActiveParticipantSessions = (): StoredParticipantSession[] => {
  try {
    const now = Date.now();
    const activeSessions: StoredParticipantSession[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        try {
          const storedData = localStorage.getItem(key);
          if (storedData) {
            const sessionData: StoredParticipantSession = JSON.parse(storedData);

            if (now <= sessionData.expiresAt) {
              activeSessions.push(sessionData);
            }
          }
        } catch (parseError) {
          // Skip corrupted data - no action needed, loop will continue
        }
      }
    }

    return activeSessions;
  } catch (error) {
    console.warn('Failed to get active participant sessions:', error);
    return [];
  }
};
